import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Brain,
  Calendar,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Target,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import type { CustomerAnalyticsQuery } from "@/gql/graphql";
import { formatCurrency } from "../../utils/customer-utils";

interface CustomerPredictiveInsightsCardProps {
  analytics?: NonNullable<CustomerAnalyticsQuery["customerAnalytics"]>;
  loading?: boolean;
}

export function CustomerPredictiveInsightsCard({
  analytics,
  loading = false,
}: CustomerPredictiveInsightsCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (loading || !analytics) {
    return <PredictiveInsightsSkeleton />;
  }

  const { predictiveInsights } = analytics;

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case "HIGH":
        return "bg-green-100 text-green-900 border-green-300 dark:bg-green-950 dark:text-green-100";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950 dark:text-yellow-100";
      case "LOW":
        return "bg-red-100 text-red-900 border-red-300 dark:bg-red-950 dark:text-red-100";
      default:
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100";
    }
  };

  const getChurnRiskColor = (probability: number) => {
    if (probability >= 70) return "text-red-600 dark:text-red-400";
    if (probability >= 40) return "text-yellow-600 dark:text-yellow-400";
    return "text-green-600 dark:text-green-400";
  };

  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case "increasing":
        return <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case "declining":
        return <TrendingUp className="w-4 h-4 text-red-600 dark:text-red-400 rotate-180" />;
      default:
        return <TrendingUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
    }
  };

  const formatDate = (date: string) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Predictive Insights
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground hover:text-foreground"
          >
            {isExpanded ? (
              <>
                <span className="mr-1">Collapse</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span className="mr-1">Expand</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Collapsed State - Key insights */}
        {!isExpanded ? (
          <>
            {/* Next Visit Prediction */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Next Visit Prediction
                </span>
                <Badge
                  variant="outline"
                  className={getConfidenceColor(predictiveInsights.nextVisit.confidence)}
                >
                  {predictiveInsights.nextVisit.confidence}
                </Badge>
              </div>
              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {predictiveInsights.nextVisit.daysFromNow === 0
                  ? "Today or overdue"
                  : `In ${predictiveInsights.nextVisit.daysFromNow} days`}
              </div>
              <div className="text-xs text-blue-700 dark:text-blue-300">
                {predictiveInsights.nextVisit.probabilityPercentage.toFixed(0)}% probability
              </div>
            </div>

            {/* Churn Risk Alert */}
            <div className="bg-muted p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Churn Risk</span>
                </div>
              </div>
              <div
                className={`text-lg font-bold ${getChurnRiskColor(predictiveInsights.churnPrediction.churnProbability)}`}
              >
                {predictiveInsights.churnPrediction.churnProbability.toFixed(0)}% Risk
              </div>
              <div className="text-xs text-muted-foreground">
                Current level:{" "}
                {predictiveInsights.churnPrediction.currentRiskLevel
                  .replace("_", " ")
                  .toLowerCase()}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Expanded State - Detailed predictions */}

            {/* Next Visit Prediction */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Next Visit Prediction
              </h4>
              <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Predicted Date</div>
                    <div className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {formatDate(predictiveInsights.nextVisit.predictedDate)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {predictiveInsights.nextVisit.daysFromNow} days from now
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Confidence</div>
                    <Badge
                      variant="outline"
                      className={getConfidenceColor(predictiveInsights.nextVisit.confidence)}
                    >
                      {predictiveInsights.nextVisit.confidence}
                    </Badge>
                    <div className="text-xs text-muted-foreground mt-1">
                      {predictiveInsights.nextVisit.probabilityPercentage.toFixed(0)}% probability
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Analysis</div>
                    <div className="text-xs text-muted-foreground">
                      {predictiveInsights.nextVisit.reasoning}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lifetime Value Forecast */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Lifetime Value Forecast
              </h4>
              <div className="border rounded-lg p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Current LTV</div>
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(
                        predictiveInsights.lifetimeValueForecast.currentLifetimeValue
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">6 Month Forecast</div>
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(
                        predictiveInsights.lifetimeValueForecast.predicted6MonthValue
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">12 Month Forecast</div>
                    <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(
                        predictiveInsights.lifetimeValueForecast.predicted12MonthValue
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Growth Trend</div>
                    <div className="flex items-center gap-1">
                      {getTrendIcon(predictiveInsights.lifetimeValueForecast.trendDirection)}
                      <span className="text-sm font-medium">
                        {predictiveInsights.lifetimeValueForecast.growthRate > 0 ? "+" : ""}
                        {predictiveInsights.lifetimeValueForecast.growthRate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={getConfidenceColor(
                      predictiveInsights.lifetimeValueForecast.confidence
                    )}
                  >
                    {predictiveInsights.lifetimeValueForecast.confidence} Confidence
                  </Badge>
                  <span className="text-xs text-muted-foreground capitalize">
                    Trend: {predictiveInsights.lifetimeValueForecast.trendDirection}
                  </span>
                </div>
              </div>
            </div>

            {/* Enhanced Churn Prediction */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Churn Risk Analysis
              </h4>
              <div className="border rounded-lg p-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Churn Probability</div>
                    <div
                      className={`text-2xl font-bold ${getChurnRiskColor(predictiveInsights.churnPrediction.churnProbability)}`}
                    >
                      {predictiveInsights.churnPrediction.churnProbability.toFixed(0)}%
                    </div>
                    <Progress
                      value={predictiveInsights.churnPrediction.churnProbability}
                      className="h-2 mt-1"
                    />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Risk Level</div>
                    <div className="text-lg font-semibold capitalize">
                      {predictiveInsights.churnPrediction.currentRiskLevel
                        .replace("_", " ")
                        .toLowerCase()}
                    </div>
                    <Badge
                      variant="outline"
                      className={getConfidenceColor(predictiveInsights.churnPrediction.confidence)}
                    >
                      {predictiveInsights.churnPrediction.confidence}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Time to Likely Churn</div>
                    <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                      {predictiveInsights.churnPrediction.daysUntilLikelyChurn} days
                    </div>
                  </div>
                </div>

                {/* Risk Factors */}
                {predictiveInsights.churnPrediction.riskFactors.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium mb-2">Risk Factors:</div>
                    <div className="flex flex-wrap gap-2">
                      {predictiveInsights.churnPrediction.riskFactors.map((factor) => (
                        <Badge key={factor} variant="destructive" className="text-xs">
                          {factor}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prevention Strategies */}
                {predictiveInsights.churnPrediction.preventionStrategies.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-2">Recommended Actions:</div>
                    <div className="space-y-1">
                      {predictiveInsights.churnPrediction.preventionStrategies.map((strategy) => (
                        <div key={strategy} className="flex items-start gap-2 text-sm">
                          <Target className="w-3 h-3 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                          <span className="text-orange-800 dark:text-orange-200">{strategy}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* AI Summary */}
            <div className="bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800 p-4 rounded-lg">
              <h4 className="font-medium mb-2 flex items-center gap-2 text-indigo-900 dark:text-indigo-100">
                <Brain className="w-4 h-4" />
                AI Summary
              </h4>
              <div className="text-sm text-indigo-800 dark:text-indigo-200 space-y-2">
                {predictiveInsights.churnPrediction.churnProbability >= 50 ? (
                  <p>⚠️ High-risk customer requiring immediate attention and retention efforts.</p>
                ) : predictiveInsights.churnPrediction.churnProbability >= 25 ? (
                  <p>📊 Moderate-risk customer - proactive engagement recommended.</p>
                ) : (
                  <p>✅ Low-risk customer with stable engagement patterns.</p>
                )}

                {predictiveInsights.lifetimeValueForecast.trendDirection === "increasing" && (
                  <p>📈 Value is trending upward - excellent opportunity for upselling.</p>
                )}

                <p>
                  🔮 Next visit expected{" "}
                  {predictiveInsights.nextVisit.daysFromNow <= 7 ? "soon" : "in the coming weeks"}
                  with {predictiveInsights.nextVisit.confidence.toLowerCase()} confidence.
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PredictiveInsightsSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-44" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/2" />
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3" />
        </div>
      </CardContent>
    </Card>
  );
}
